import React, { FC, MouseEvent, useContext, useState } from "react";

import {
	GetLanguageValueAsString,
	Translate,
} from "@/app/application/Translate";
import { TranslationContext } from "@/app/providers";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
	Box,
	Button,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Typography,
} from "@mui/material";

type Props = {
	sortOrder: "ASC" | "DESC";
	setSortOrder: (order: "ASC" | "DESC") => void;
	disabled?: boolean;
};

const SortMenu: FC<Props> = ({ sortOrder, setSortOrder, disabled = false }) => {
	const translation = useContext(TranslationContext);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	const handleClick = (event: MouseEvent<HTMLElement>) => {
		if (disabled) return;
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleMenuItemClick = (order: "ASC" | "DESC") => {
		setSortOrder(order);
		handleClose();
	};

	return (
		<>
			<Button
				id="sort-menu-button"
				aria-label={GetLanguageValueAsString(
					translation,
					"Common.Sort.Menu",
					"Sort menu",
				)}
				aria-controls={open ? "sort-menu" : undefined}
				aria-haspopup="menu"
				aria-expanded={open ? "true" : undefined}
				onClick={handleClick}
				disabled={disabled}
				variant="outlined"
				color="primary"
				size="small"
				endIcon={<KeyboardArrowDownIcon />}
				sx={{
					height: 32,
					borderRadius: 2,
					textTransform: "none",
					fontWeight: 800,
				}}
			>
				<Translate String="Common.Sort">Sort</Translate>
			</Button>

			<StyledMenu
				id="sort-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				MenuListProps={{
					"aria-labelledby": "sort-menu-button",
					dense: true,
				}}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				PaperProps={{ sx: { minWidth: 220 } }}
			>
				<Box px={1.5} pt={1} pb={0.5}>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ letterSpacing: 0.6 }}
					>
						<Translate String="Common.Filters.Heading">Sort Order</Translate>
					</Typography>
				</Box>

				<MenuItem
					selected={sortOrder === "ASC"}
					onClick={() => handleMenuItemClick("ASC")}
				>
					<ListItemIcon>
						<ArrowUpwardIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={<Translate String="Common.Filters.ASC" />}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Common.Filters.ASC.Description">
									Ascending order
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>

				<MenuItem
					selected={sortOrder === "DESC"}
					onClick={() => handleMenuItemClick("DESC")}
				>
					<ListItemIcon>
						<ArrowDownwardIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={<Translate String="Common.Filters.DESC" />}
						secondary={
							<Typography variant="caption" color="text.secondary">
								<Translate String="Common.Filters.DESC.Description">
									Descending order
								</Translate>
							</Typography>
						}
					/>
				</MenuItem>
			</StyledMenu>
		</>
	);
};

export default SortMenu;
